/**
 * Quantity Selector Component
 * Plus/minus quantity control
 */

import React from 'react';
import styled from 'styled-components';

interface QuantitySelectorProps {
  quantity: number;
  onChange: (quantity: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const QuantitySelector: React.FC<QuantitySelectorProps> = ({
  quantity,
  onChange,
  min = 1,
  max = 99,
  disabled = false,
  size = 'medium'
}) => {
  const handleDecrease = () => {
    if (quantity > min && !disabled) {
      onChange(quantity - 1);
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    }
  };

  const handleIncrease = () => {
    if (quantity < max && !disabled) {
      onChange(quantity + 1);
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= min && value <= max) {
      onChange(value);
    }
  };

  return (
    <Container $size={size} $disabled={disabled}>
      <Button
        onClick={handleDecrease}
        disabled={disabled || quantity <= min}
        $size={size}
      >
        <svg width={size === 'small' ? 14 : 18} height={size === 'small' ? 14 : 18} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13H5v-2h14v2z" />
        </svg>
      </Button>

      <QuantityInput
        type="number"
        value={quantity}
        onChange={handleInputChange}
        min={min}
        max={max}
        disabled={disabled}
        $size={size}
      />

      <Button
        onClick={handleIncrease}
        disabled={disabled || quantity >= max}
        $size={size}
      >
        <svg width={size === 'small' ? 14 : 18} height={size === 'small' ? 14 : 18} viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
        </svg>
      </Button>
    </Container>
  );
};

const Container = styled.div<{ $size: string; $disabled: boolean }>`
  display: flex;
  align-items: center;
  border-radius: ${props => props.$size === 'small' ? '6px' : '8px'};
  border: 1px solid var(--tg-theme-hint-color, #e0e0e0);
  overflow: hidden;
  opacity: ${props => props.$disabled ? 0.5 : 1};
`;

const Button = styled.button<{ $size: string }>`
  width: ${props => {
    switch (props.$size) {
      case 'small': return '28px';
      case 'large': return '40px';
      default: return '34px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'small': return '28px';
      case 'large': return '40px';
      default: return '34px';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tg-theme-secondary-bg-color, #f0f0f0);
  border: none;
  color: var(--tg-theme-text-color, #000000);
  cursor: pointer;
  transition: background 0.2s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    background: var(--tg-theme-hint-color, #e0e0e0);
  }
`;

const QuantityInput = styled.input<{ $size: string }>`
  width: ${props => {
    switch (props.$size) {
      case 'small': return '32px';
      case 'large': return '50px';
      default: return '40px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'small': return '28px';
      case 'large': return '40px';
      default: return '34px';
    }
  }};
  border: none;
  text-align: center;
  font-size: ${props => props.$size === 'small' ? '13px' : '15px'};
  font-weight: 500;
  color: var(--tg-theme-text-color, #000000);
  background: var(--tg-theme-bg-color, #ffffff);
  outline: none;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export default QuantitySelector;
